import React, { useEffect } from "react";
import { observer } from "mobx-react";
import copilotStore, { TABLE_PAGE_SIZES } from "Stores/copilotStore";
import PaginationComponent from "Components/common/PaginationComponent";
import ProspectTable from "../ProspectTable";

// LAZY: ContactActionBar reuses the Contacts page's buttons and popups. A
// static import would pull that page's module tree into the copilot bundle
// through BlockRenderer, and a throw during its module init would leave the
// copilot rendering nothing at all — clean compile, no console error.
const ContactActionBar = React.lazy(() => import("../ContactActionBar"));

/**
 * Saved contacts as a compact Contacts page, inline in the thread.
 *
 * Everything here is the product's own machinery rather than a copilot-only
 * reimplementation: ProspectTable draws the rows, PaginationComponent does the
 * paging, and the bulk actions are the Contacts page's real buttons. Paging
 * goes through the existing tableId round trip, so moving between pages,
 * changing page size and acting on a selection never involve the model.
 */
const ContactTableBlock = observer(({ block }) => {
  useEffect(() => {
    if (!block.tableId) return;
    copilotStore.registerTable(block);
    const table = copilotStore.tables.get(block.tableId);
    if (table && !table.rows.length && !table.loading) {
      copilotStore.fetchTablePage(block.tableId, 1);
    }
    // Keyed on tableId, not block identity: the block is an observable that
    // changes on every patch, and re-registering per patch would refetch page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.tableId]);

  const tableId = block.tableId;
  const table = tableId ? copilotStore.tables.get(tableId) : null;
  if (!table) return null;

  const totalPages = Math.max(1, Math.ceil(table.totalCount / table.pageSize));
  const pageIds = (table.rows || []).map(row => row.id).filter(Boolean);
  const pageAllSelected =
    pageIds.length > 0 &&
    pageIds.every(id => (table.selectedIds || []).indexOf(id) >= 0);
  const offerAllMatching =
    table.selectionScope !== "page" &&
    pageAllSelected &&
    table.totalCount > pageIds.length;
  const hasSelection =
    table.allMatchingSelected || (table.selectedIds || []).length > 0;


  return (
    <div className="cpContactTable">
      <div className="cpContactTable__head">
        <span className="cpContactTable__title">{block.title || "Contacts"}</span>
        <span className="cpContactTable__count">
          {table.totalCount.toLocaleString()} contacts
        </span>
      </div>

      {/* Height is reserved whether or not anything is selected: letting the
          action bar appear and disappear would shove the table down the page
          under the user's cursor mid-click. */}
      <div className="cpContactTable__actionSlot">
        {hasSelection ? (
          <React.Suspense fallback={null}>
            <ContactActionBar tableId={tableId} />
          </React.Suspense>
        ) : (
          <span className="cpContactTable__actionHint">
            Select contacts to add to a sequence, enrich or export.
          </span>
        )}

        {/* Rides in the SAME reserved row rather than adding one of its own —
            a second row would reflow the table exactly as the bar used to. */}
        {offerAllMatching && (
          <span className="cpContactTable__selectAll">
            {table.allMatchingSelected ? (
              <button
                type="button"
                onClick={() => copilotStore.selectAllMatching(tableId, false)}
              >
                Clear selection of all {table.totalCount.toLocaleString()}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => copilotStore.selectAllMatching(tableId, true)}
              >
                Select all {table.totalCount.toLocaleString()} matching
              </button>
            )}
          </span>
        )}
      </div>

      <div className="cpContactTable__body">
        <ProspectTable
          table={table}
          tableId={tableId}
          onRowClick={row => copilotStore.openContactFromTable(tableId, row)}
        />
      </div>

      <div className="cpContactTable__foot">
        <div className="cpContactTable__pageSize">
          <span>Rows</span>
          {TABLE_PAGE_SIZES.map(size => (
            <button
              type="button"
              key={size}
              className={table.pageSize === size ? "selected" : ""}
              disabled={table.loading}
              onClick={() => copilotStore.setTablePageSize(tableId, size)}
            >
              {size}
            </button>
          ))}
        </div>
        {totalPages > 1 && (
          <PaginationComponent
            totalPages={totalPages}
            currentPage={table.currentPage}
            loading={table.loading}
            callback={({ selected }) =>
              copilotStore.fetchTablePage(tableId, selected + 1)
            }
          />
        )}
      </div>
    </div>
  );
});

export default ContactTableBlock;
